import { HubConnectionBuilder, HubConnection } from "@aspnet/signalr";
import React, { useEffect, useRef, useState } from "react";
import ImagerApi from "../../../api/ImagerApi";
import config from "../../../config";
import { ImagerState } from "../../../Model/ModuleState";
import { getModuleState } from "../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import ImagingArmMovementControlV2 from "../../Imager/ImagingArmMovementControlV2";
import ImagingArmSpeedSlider from "../../Imager/ImagingArmSpeedSlider";
import WellBasedMovement from "../../Imager/WellBasedMovement";
import WellControlValueLabels from "./WellControlValueLabels";
import { sendNotification } from "../../../redux/actions/NotificationAction";
import { ENotificationType } from "../../../Model/ENotificationType";
import { EMoveArmDirection } from "../../../Model/EMoveImagingArmButton";
import SwitchButton from "../../Buttons/SwitchButton";
import { getShowWellOverlay } from "../../../redux/reducers/LiveImageReducer";
import { useDispatch } from "react-redux";
import { setShowWellOverlay } from "../../../redux/actions/LiveImageAction";
import { EImagingModuleStatus } from "../../../Model/ModuleState";
import { getModules } from "../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../Model/EModuleType";

type Props = {
  style?: React.CSSProperties;
  disabled: boolean;
  imagerIdentifier: string;
  wellLimitEnabled: boolean;
};

function getInitialState(imagerState?: ImagerState) {
  if (imagerState?.plate) {
    const cols = imagerState.plate.plateType.numberOfColumn;
    const col = Math.floor((imagerState.wellNumber - 1) / cols);
    const row = imagerState.wellNumber - 1 - col * cols;
    return {
      selectedColumn: col,
      selectedRow: row,
      selectedDrop: imagerState.dropNumber,
      cols: cols,
      rows: imagerState.plate.plateType.numberOfRow,
    };
  } else {
    return {
      selectedColumn: 0,
      selectedRow: 0,
      selectedDrop: 1,
      cols: 12,
      rows: 8,
    };
  }
}

interface HubRefParams {
  hubConnection: HubConnection | null;
}

const startSignalRConnection = (connection: HubConnection) =>
  connection
    .start()
    .then(() => {
      console.info("Arm SignalR Connected");
    })
    .catch((err) =>
      sendNotification(
        ENotificationType.Error,
        "Arm SignalR Connection Error: " + err
      )
    );

const IMAGER_ARM_SPEED = "IMAGER_ARM_SPEED";

function getSpeed(key: string) {
  const value = localStorage.getItem(key);
  if (value != null) {
    return Number.parseInt(value);
  } else {
    return 3;
  }
}

const WellControl = ({
  style = {},
  disabled,
  imagerIdentifier,
  wellLimitEnabled,
}: Props) => {
  const dispatch = useDispatch();
  const moduleState = useTypedSelector((state) =>
    getModuleState(state, imagerIdentifier)
  );
  const checkIfAnyPlateOnImager = useTypedSelector((state) =>
    state.moduleStateStore.moduleStateById[imagerIdentifier].imager
        ?.state === EImagingModuleStatus.Imageable ? true : false
  );
  if(!checkIfAnyPlateOnImager)
  {
    dispatch(setShowWellOverlay(false));
  }
  const wellOverlay = useTypedSelector((state) => getShowWellOverlay(state));
  const imagerState = moduleState.imager;
  const initialState = getInitialState(imagerState);
  const [selectedColNumber, setSelectedColNumber] = useState(
    initialState.selectedColumn
  );
  const [selectedRowNumber, setSelectedRowNumber] = useState(
    initialState.selectedRow
  );
  const [selectedDropNumber, setSelectedDropNumber] = useState(
    initialState.selectedDrop
  );
  const [cols, setCols] = useState(initialState.cols);
  const [rows, setRows] = useState(initialState.rows);
  const [speed, setSpeed] = useState(getSpeed(IMAGER_ARM_SPEED));

  console.log(
    "DEBUG",
    { selectedColNumber },
    { selectedRowNumber },
    { selectedDropNumber }
  );

  useEffect(() => {
    localStorage.setItem(IMAGER_ARM_SPEED, speed.toString());
  }, [speed]);

  const hubRef = useRef<HubRefParams>({ hubConnection: null });

  useEffect(() => {
    const urlConnection = config.SIGNALR_URL + "/arm";
    const hubConnection = new HubConnectionBuilder()
      .withUrl(urlConnection)
      .build();
    hubRef.current.hubConnection = hubConnection;

    hubConnection.onclose(() => {
      console.log("SignalR connection lost");
      setTimeout(() => {
        console.log("SignalR connection restarted");
        if (hubConnection != null) {
          startSignalRConnection(hubConnection);
        }
      }, 5000);
    });

    startSignalRConnection(hubConnection);

    return () => {
      hubConnection?.stop();
    };
  }, []);

  useEffect(() => {
    if (imagerState?.plate) {
      const selectedRow = Math.floor((imagerState.wellNumber - 1) / cols);
      const selectedCol = imagerState.wellNumber - 1 - selectedRow * cols;
      setSelectedColNumber(selectedCol);
      setSelectedRowNumber(selectedRow);
      setSelectedDropNumber(imagerState.dropNumber);
    }
  }, [
    imagerState,
    imagerState?.wellNumber,
    imagerState?.dropNumber,
    cols,
    imagerState?.plate?.barcode,
  ]);

  useEffect(() => {
    if (imagerState?.plate) {
      setCols(imagerState.plate.plateType.numberOfColumn);
      setRows(imagerState.plate.plateType.numberOfRow);
    } else {
      setCols(12);
      setRows(8);
    }
  }, [imagerState, imagerState?.plate?.barcode]);

  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  const onToggleOverlay = () => {
    dispatch(setShowWellOverlay(!wellOverlay));
  };

  const onWellChanged = ({ col, row }: { col: number; row: number }) => {
    setSelectedColNumber(col);
    setSelectedRowNumber(row);
    const wellNumber = row * cols + col + 1;
    ImagerApi.moveToWell(imagerIdentifier, wellNumber, selectedDropNumber);
  };

  const onButtonDown = (buttonIdentifier: EMoveArmDirection) => {
    if (wellLimitEnabled) {
      hubRef.current.hubConnection?.send(
        "ImagerArmMoveToWellLimit",
        imagerIdentifier,
        buttonIdentifier,
        speed
      );
    } else {
      hubRef.current.hubConnection?.send(
        "ImagerArmMoveToTravelLimit",
        imagerIdentifier,
        buttonIdentifier,
        speed
      );
    }
  };

  const onButtonUp = () => {
    hubRef.current.hubConnection?.send("ImagerArmStop", imagerIdentifier);
  };

  const titleStyle = {
    minHeight: 36,
    fontSize: "14px",
    lineHeight: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#4F4F4F",
  };

  return (
    <div
      style={{
        height: "calc(100% - 22px)",
        width: "calc(100% - 22px)",
        display: "grid",
        gridTemplateAreas: `'well arm'
                            'position position '`,
        gridTemplateColumns: "calc(100% - 175px) 175px",
        gridTemplateRows: "calc(100% - 36px) 36px",
        border: "1px solid #E0E0E0",
        borderRadius: "10px",
        margin: "11px",
        padding: "10px 13px 10px 13px",
      }}
    >
      <div
        style={{
          gridArea: "well",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            fontFamily: "Arial",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "14px",
            marginBottom: "7px",
            marginTop:"3px",
          }}
        >
          <SwitchButton
            isOn={wellOverlay}
            onToggle={() => onToggleOverlay()}
            withLetter={true}
            style={{
              marginRight: "8px"
            }}
          />
          <span>Enable Well Display on Live View </span>
        </div>
        <WellBasedMovement
          cols={cols}
          rows={rows}
          selectedCol={selectedColNumber}
          selectedRow={selectedRowNumber}
          onChange={onWellChanged}
        />
      </div>

      <div
        style={{
          gridArea: "arm",
        }}
      >
        <div style={titleStyle}>Fine Tuning Movement</div>
        <ImagingArmMovementControlV2
          onMouseDown={onButtonDown}
          onMouseUp={onButtonUp}
          style={{
            marginBottom: 10,
          }}
        />
        <ImagingArmSpeedSlider
          disabled={disabled}
          value={speed}
          onValueChanged={setSpeed}
        />
      </div>

      <WellControlValueLabels
        position={imagerState.cameraPositionRelativeToWell}
        style={{
          gridArea: "position",
        }}
      />
    </div>
  );
};

export default WellControl;
